// Retired vendors: "may be ordered from" and "may describe existing stock"
// are two different questions.
//
// When a restaurant stops buying from Sysco, Sysco must disappear from
// new-order eligibility immediately — the automatic vendor selection, the
// Savings Review candidate list, MOQ resolution, allocation — while Sysco's
// old package definition stays usable for counting the cases still on the
// shelf and for explaining historical counts. So retirement is checked ONLY
// at the ordering gates; the count-presentation resolver and the package
// ladder deliberately never consult it.
//
// Retirement is a VENDOR state, not an option state. The per-option flags
// (`status`, `is_hidden_from_allocation`) answer different questions — one
// product discontinued, one product hidden — and cascading a vendor change
// into hundreds of option rows would make un-retiring guesswork. The vendor's
// own row already carries the answer (`vendors.status` / `is_active` /
// `deleted_at`); options carry it along as an embedded `vendor` object or
// flat `vendor_*` fields stamped at fetch time.
//
// An option that carries NO vendor state is treated as not retired — the
// columns may simply not have been selected, and "we did not look" must not
// disqualify a vendor. Mirrored in app/javascript/utils/purchaseOptions.js
// (vendorIsRetired); the parity test pins the two together.
//
// Pure ESM. No Deno APIs, no I/O.

const text = (v) => (v == null ? "" : String(v).trim().toLowerCase());

/**
 * Vendor `status` values that mean "no longer a supplier". `merged` rows are
 * husks left behind by vendor-alias resolution; `stub` and `needs_info` are
 * onboarding states for vendors not yet filled in, and are NOT retirement.
 */
export const RETIRED_VENDOR_STATUSES = Object.freeze(["inactive", "merged"]);

/**
 * True when the vendor behind this option is retired — deleted, deactivated,
 * or merged away — and the option must not enter any NEW-order path.
 *
 * Reads, in order: an embedded `vendor` object (a `vendors` join), then flat
 * `vendor_status` / `vendor_is_active` / `vendor_deleted_at` fields. Absent
 * state is not retirement.
 */
export function vendorIsRetired(option) {
  if (!option) return false;
  const vendor = option.vendor && typeof option.vendor === "object" ? option.vendor : null;

  const deletedAt = vendor ? vendor.deleted_at : option.vendor_deleted_at;
  if (deletedAt != null && String(deletedAt) !== "") return true;

  const isActive = vendor ? vendor.is_active : option.vendor_is_active;
  if (isActive === false) return true;

  const status = text(vendor ? vendor.status : option.vendor_status);
  return RETIRED_VENDOR_STATUSES.includes(status);
}
